import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useCreateCaso } from '@/hooks/useCasos';
import { GradientSelector } from './GradientSelector';
import { IconSelector } from './IconSelector';

const formSchema = z.object({
  title: z.string().min(3, 'O nome deve ter no mínimo 3 caracteres'),
  description: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface CreateCasoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentFolderId?: string | null;
}

export function CreateCasoDialog({ open, onOpenChange, currentFolderId }: CreateCasoDialogProps) {
  const navigate = useNavigate();
  const createCaso = useCreateCaso();
  const [coverGradient, setCoverGradient] = useState('gradient-1');
  const [icon, setIcon] = useState('scale');

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: '',
      description: '',
    },
  });

  const onSubmit = async (values: FormValues) => {
    try {
      const newCaso = await createCaso.mutateAsync({
        title: values.title,
        description: values.description,
        cover_gradient: coverGradient,
        icon,
        folder_id: currentFolderId,
      });
      form.reset();
      setCoverGradient('gradient-1');
      setIcon('scale');
      onOpenChange(false);
      navigate(`/casos/${newCaso.id}`);
    } catch (error) {
      console.error('Error creating caso:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="sm:max-w-md flex flex-col max-h-[85vh]"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Criar Novo Caso</DialogTitle>
          <DialogDescription>
            Adicione um nome e uma descrição opcional para o seu caso.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col overflow-hidden">
            <div className="overflow-y-auto flex-1 pr-1" style={{ maxHeight: '400px' }}>
              <div className="space-y-4 pb-4 px-3">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nome *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Digite o nome do caso"
                          {...field}
                          autoFocus
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Descrição (opcional)</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Adicione uma descrição..."
                          rows={3}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <GradientSelector 
                  value={coverGradient}
                  onChange={setCoverGradient}
                />

                <IconSelector 
                  value={icon}
                  onChange={setIcon}
                />
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0 pt-4 border-t mt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  form.reset();
                  onOpenChange(false);
                }}
                disabled={createCaso.isPending}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={createCaso.isPending}>
                {createCaso.isPending ? 'Criando...' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
